// @ts-check
import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import createMutationRequest from 'utils/createMutationRequest';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { useUsersQuery } from '../Users/queries';
import { useGroupsQuery } from '../Groups/queries';
import { group2ug } from './queries';
import { ResourceRelationLevel } from 'utils/types';
import { useAuthService } from 'services/AuthService/AuthService';
import GroupRelationTable from './GroupRelationTable';
import { VisibleColumns } from 'components/RelationTable/RelationTable';


const order = [ResourceRelationLevel.owner, ResourceRelationLevel.manager, ResourceRelationLevel.member];


export const createGroup2UserRelation = (groupId, userId, relation) => {
  return {
    resourceType: 'group',
    resourceId: groupId,
    relation: relation,
    subjectType: 'user',
    subjectId: userId
  };
};

export const createGroup2GroupRelation = (parentGroupId, childGroupId, relation) => {
  return {
    resourceType: 'group',
    resourceId: parentGroupId,
    relation: relation,
    subjectType: 'group',
    subjectId: childGroupId
  };
};

const GroupToUserRelations = ({ groupId }) => {
  const api = useAPI();
  const usersQuery = useUsersQuery();
  const groupsQuery = useGroupsQuery();
  const queryClient = useQueryClient();
  const { user } = useAuthService();

  const relationQueryData = {
    resourceType: 'group',
    optionalResourceId: groupId
  };

  const relationQuery = useQuery(group2ug.group(relationQueryData), createQueryRequest(api.core.findRelationships), {
    enabled: !!groupId
  });

  const relations = useMemo(() => {
    return _.map(_.uniqBy(_.orderBy(/** @type {object} */(relationQuery.data), data => _.indexOf(order, data.relation)), 'subjectId'), relation => {
      return {
        id: relation.subjectId,
        relation: relation.relation
      };
    });
  }, [relationQuery.data]);

  const users = useMemo(() => {
    return _.map(usersQuery.data, user => {
      const relation = _.find(relations, { id: user.id });
      return {
        ...user,
        type: 'user',
        relation: relation?.relation ?? ResourceRelationLevel.none
      };
    });
  }, [usersQuery.data, relations]);

  const groups = useMemo(() => {
    const withoutSelf = _.filter(/** @type {object} */(groupsQuery.data), group => group.id !== groupId);
    return _.map(withoutSelf, group => {
      const relation = _.find(relations, { id: group.id });
      return {
        ...group,
        type: 'group',
        relation: relation?.relation ?? ResourceRelationLevel.none
      };
    });
  }, [groupId, groupsQuery.data, relations]);

  const updateRelations = useMutation({
    mutationFn: createMutationRequest(api.core.createRelationship),
    onMutate: (relation) => {
      const oldData = queryClient.getQueryData(group2ug.group(relationQueryData));
      queryClient.setQueryData(group2ug.group(relationQueryData), old => {
        return [...old, relation];
      });
      return { relation, oldData };
    },
    onSuccess: () => {
      relationQuery.refetch();
    },
    onError: (error, unusedVariables, context) => {
      console.error(error);
      queryClient.setQueryData(group2ug.group(relationQueryData), context?.oldData);
      relationQuery.refetch();
    }
  });

  const deleteRelations = useMutation({
    mutationFn: createMutationRequest(api.core.deleteRelationship),
    onSuccess: () => {
      relationQuery.refetch();
    },
    onError: () => {
      relationQuery.refetch();
    }
  });

  return (
    <GroupRelationTable
      groupId={groupId}
      resources={[...users, ...groups]}
      visibleColumns={[VisibleColumns.displayName, VisibleColumns.id, VisibleColumns.type, VisibleColumns.relation]}
      isLoading={usersQuery.isLoading || relationQuery.isLoading || groupsQuery.isLoading}
      updateRelationsFn={updateRelations.mutate}
      deleteRelationsFn={deleteRelations.mutate}
      currentUserId={user.userId}
    />
  );
};

export default GroupToUserRelations;
